import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminDatabase } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { absoluteTime, bytes, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/databases")({
  component: DatabasesScreen,
});

/** A backup older than this needs somebody. Nightly plus most of a day's slack. */
const STALE_HOURS = 36;

/**
 * Managed databases, ordered by how stale their backups are.
 *
 * Backups run nightly. Nothing could see whether they ran — the rows were in
 * database_backups all along and no screen read them, so "is this customer's
 * data safe" was a question answered by opening psql, or not at all.
 */
function DatabasesScreen() {
  const databases = useQuery({
    queryKey: ["platform-databases"],
    queryFn: ({ signal }) =>
      api.get<{ databases: AdminDatabase[] | null }>("/admin/databases", { limit: 300 }, signal),
    refetchInterval: 60000,
  });

  const list = databases.data?.databases ?? [];
  const unprotected = list.filter((d) => d.backup_enabled && stale(d.last_backup_at));
  const off = list.filter((d) => !d.backup_enabled);

  return (
    <div className="flex flex-col gap-4">
      {(unprotected.length > 0 || off.length > 0) && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            {unprotected.length > 0 &&
              `${unprotected.length} ${unprotected.length === 1 ? "database has" : "databases have"} no recent backup`}
            {unprotected.length > 0 && off.length > 0 && " · "}
            {off.length > 0 && `${off.length} with backups switched off`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            Backups are meant to run nightly. Anything older than {STALE_HOURS} hours has missed
            one.
          </p>
        </div>
      )}

      <Panel
        title="Managed databases"
        description="Stalest backup first, and a database that has never had one sorts above all of them."
        padded={false}
      >
        {databases.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={5} columns={6} />
          </div>
        ) : databases.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={databases.error} onRetry={() => void databases.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No databases" description="Nobody has created one." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[22%]">Database</Th>
                  <Th width="w-[17%]">Tenant</Th>
                  <Th width="w-[11%]">State</Th>
                  <Th width="w-[13%]" numeric>
                    Size
                  </Th>
                  <Th width="w-[15%]" align="right">
                    Last backup
                  </Th>
                  <Th width="w-[11%]" numeric>
                    Backup size
                  </Th>
                  <Th width="w-[11%]">Backups</Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((d) => {
                  const bad = d.backup_enabled && stale(d.last_backup_at);
                  return (
                    <Tr
                      key={d.id}
                      className={
                        bad || !d.backup_enabled
                          ? "bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]"
                          : undefined
                      }
                    >
                      <Td
                        clip
                        title={`${d.display_name} · PostgreSQL ${d.major_version} · ${d.project_name ?? ""} ${d.environment_name ?? ""}`}
                        secondary={`pg${d.major_version}`}
                      >
                        {d.display_name}
                      </Td>
                      <Td clip title={d.organisation_name}>
                        <Link
                          to="/admin/$orgId"
                          params={{ orgId: d.organisation_id }}
                          className="hover:text-[var(--primary)]"
                        >
                          {d.organisation_name}
                        </Link>
                      </Td>
                      <Td clip title={d.state_reason || d.state}>
                        <Chip tone={d.state === "RUNNING" ? "up" : "warn"}>
                          {d.state}
                        </Chip>
                      </Td>
                      <Td numeric className="mono text-[13px]">
                        {d.instances}× {d.storage_gib}Gi
                      </Td>
                      <Td
                        align="right"
                        title={d.last_backup_at ? absoluteTime(d.last_backup_at) : undefined}
                      >
                        {d.last_backup_at ? (
                          <span className={bad ? "text-[var(--danger)]" : undefined}>
                            {shortTime(d.last_backup_at)}
                          </span>
                        ) : (
                          <span className="text-[var(--danger)]">never</span>
                        )}
                      </Td>
                      <Td numeric>
                        {d.last_backup_bytes === undefined ? "—" : bytes(d.last_backup_bytes)}
                      </Td>
                      <Td>
                        {!d.backup_enabled ? (
                          <Chip tone="down">off</Chip>
                        ) : d.failed_backups > 0 ? (
                          <Chip tone="warn">{d.failed_backups} failed</Chip>
                        ) : (
                          <span className="text-[var(--text-muted)]">
                            {d.backup_retention_days}d
                          </span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function stale(iso: string | undefined): boolean {
  if (!iso) return true;
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return true;
  return Date.now() - at > STALE_HOURS * 3600000;
}
